import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TotalWeightWidget } from "@redotech/redo-merchant-app-common/components/weight-widget/total-weight-widget";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  RoundWeightActionStep,
  WeightRoundingType,
} from "@redotech/redo-model/advanced-flow/oms/actions";
import {
  Weight,
  WeightFormat,
  WeightUnit,
} from "@redotech/redo-model/outbound-labels/util";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { PartialWithRequiredType } from ".";

const roundingTypeOptions: RedoListItem<WeightRoundingType>[] = [
  {
    id: WeightRoundingType.ROUND_UP,
    type: "text",
    text: "Round up",
    value: WeightRoundingType.ROUND_UP,
  },
  {
    id: WeightRoundingType.ROUND_DOWN,
    type: "text",
    text: "Round down",
    value: WeightRoundingType.ROUND_DOWN,
  },
];

export const RoundWeightInput = memo(function RoundWeightInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RoundWeightActionStep>;
  setStep: (step: PartialWithRequiredType<RoundWeightActionStep>) => void;
}) {
  const team = useRequiredContext(TeamContext);

  const onRoundingTypeChange = useHandler(
    ({ item }: { item: RedoListItem<WeightRoundingType> }) => {
      setStep({ ...step, roundingType: item.value });
    },
  );

  const onMinimumWeightChange = useHandler((minimumWeight: Weight) => {
    setStep({ ...step, minimumWeight });
  });

  // Convert step.minimumWeight to a Weight object
  const currentMinimumWeight =
    step.minimumWeight && step.minimumWeight.unit && step.minimumWeight.value
      ? {
          unit: step.minimumWeight.unit as WeightUnit,
          value: step.minimumWeight.value,
        }
      : { unit: WeightUnit.POUND, value: 1 };

  const selectedRoundingType = roundingTypeOptions.find(
    (option) => option.value === step.roundingType,
  );

  return (
    <Flex align="stretch" dir="column" flex={1} gap="md">
      <Flex align="center" dir="row" gap="md" wrap="wrap">
        <RedoSingleSelectDropdownInput<WeightRoundingType>
          fitToAnchor={false}
          flexGrow={false}
          options={roundingTypeOptions}
          optionSelected={onRoundingTypeChange}
          placeholder="Select rounding type"
          selectedItem={selectedRoundingType}
          size="xs"
        />
        <Text fontSize="xs" fontWeight="medium">
          when weight is at least
        </Text>
      </Flex>
      <Flex align="stretch" dir="column" flex={1}>
        <TotalWeightWidget
          disabled={false}
          onWeightChange={onMinimumWeightChange}
          weight={currentMinimumWeight}
          weightFormat={
            team?.settings?.outboundLabels?.weightFormat ??
            WeightFormat.IMPERIAL
          }
        />
      </Flex>
    </Flex>
  );
});
